import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const Documentation = () => {
    const [selectedDoc, setSelectedDoc] = useState('README');
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);

    const docs = [
        { id: 'README', title: '📖 README - Documentação Principal', file: 'README.md' },
        { id: 'QUICKSTART', title: '🚀 Quick Start - Guia Rápido', file: 'QUICKSTART.md' },
        { id: 'APPLICATION-SUMMARY', title: '📦 Resumo da Aplicação', file: 'APPLICATION-SUMMARY.md' },
        { id: 'TROUBLESHOOTING', title: '🔧 Troubleshooting - Solução de Problemas', file: 'TROUBLESHOOTING.md' },
        { id: 'MIGRATION-COMPLETE-REPORT', title: '📊 Relatório de Migração Completo', file: 'MIGRATION-COMPLETE-REPORT.md' },
        { id: 'CNAME-FIX-REPORT', title: '🔧 Relatório de Correção de CNAMEs', file: 'CNAME-FIX-REPORT.md' },
        { id: 'VALIDATION-FINAL', title: '✅ Validação Final', file: 'VALIDATION-FINAL.md' },
        { id: 'MIGRATION-COMPLETE', title: '📋 Migração Concluída', file: 'MIGRATION-COMPLETE.md' },
        { id: 'summary-all-domains', title: '🌐 Resumo de Todos os Domínios', file: 'summary-all-domains.md' }
    ];

    useEffect(() => {
        loadDocument(selectedDoc);
    }, [selectedDoc]);

    const loadDocument = async (docId) => {
        setLoading(true);
        try {
            const doc = docs.find(d => d.id === docId);
            if (doc) {
                const response = await fetch(`/docs/${doc.file}`);
                const text = await response.text();
                setContent(text);
            }
        } catch (error) {
            setContent(`# Erro ao Carregar Documento\n\nNão foi possível carregar o documento. Erro: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className="card">
                <div className="card-header">
                    <h2 className="card-title">📚 Documentação</h2>
                    <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                        Toda a documentação da migração e da aplicação em um só lugar
                    </p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '1.5rem' }}>
                {/* Sidebar com lista de documentos */}
                <div className="card" style={{ height: 'fit-content', position: 'sticky', top: '2rem' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#374151' }}>
                        Documentos Disponíveis
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {docs.map(doc => (
                            <button
                                key={doc.id}
                                className={`doc-nav-item ${selectedDoc === doc.id ? 'active' : ''}`}
                                onClick={() => setSelectedDoc(doc.id)}
                                style={{
                                    padding: '0.75rem',
                                    background: selectedDoc === doc.id ? '#fff5e6' : 'transparent',
                                    border: selectedDoc === doc.id ? '1px solid #f38020' : '1px solid transparent',
                                    borderRadius: '6px',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: selectedDoc === doc.id ? 600 : 400,
                                    color: selectedDoc === doc.id ? '#f38020' : '#4b5563',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    if (selectedDoc !== doc.id) {
                                        e.target.style.background = '#f9fafb';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (selectedDoc !== doc.id) {
                                        e.target.style.background = 'transparent';
                                    }
                                }}
                            >
                                {doc.title}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Conteúdo do documento */}
                <div className="card">
                    {loading ? (
                        <div className="loading">
                            <div className="spinner"></div>
                            <span>Carregando documento...</span>
                        </div>
                    ) : (
                        <div className="markdown-content">
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    h1: ({node, ...props}) => <h1 style={{ fontSize: '2rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '1rem', color: '#111827', borderBottom: '2px solid #e5e7eb', paddingBottom: '0.5rem' }} {...props} />,
                                    h2: ({node, ...props}) => <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem', color: '#1f2937' }} {...props} />,
                                    h3: ({node, ...props}) => <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginTop: '1.25rem', marginBottom: '0.75rem', color: '#374151' }} {...props} />,
                                    h4: ({node, ...props}) => <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '1rem', marginBottom: '0.5rem', color: '#4b5563' }} {...props} />,
                                    p: ({node, ...props}) => <p style={{ lineHeight: '1.7', marginBottom: '1rem', color: '#4b5563' }} {...props} />,
                                    ul: ({node, ...props}) => <ul style={{ listStyle: 'disc', marginLeft: '1.5rem', marginBottom: '1rem', lineHeight: '1.7' }} {...props} />,
                                    ol: ({node, ...props}) => <ol style={{ listStyle: 'decimal', marginLeft: '1.5rem', marginBottom: '1rem', lineHeight: '1.7' }} {...props} />,
                                    li: ({node, ...props}) => <li style={{ marginBottom: '0.5rem', color: '#4b5563' }} {...props} />,
                                    code: ({node, inline, ...props}) =>
                                        inline ?
                                            <code style={{ background: '#f3f4f6', padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.875em', color: '#ef4444', fontFamily: 'monospace' }} {...props} /> :
                                            <code style={{ display: 'block', background: '#1f2937', color: '#e5e7eb', padding: '1rem', borderRadius: '6px', overflow: 'auto', fontSize: '0.875rem', fontFamily: 'monospace', lineHeight: '1.5' }} {...props} />,
                                    pre: ({node, ...props}) => <pre style={{ marginBottom: '1rem' }} {...props} />,
                                    blockquote: ({node, ...props}) => <blockquote style={{ borderLeft: '4px solid #f38020', paddingLeft: '1rem', marginLeft: 0, marginBottom: '1rem', color: '#6b7280', fontStyle: 'italic' }} {...props} />,
                                    table: ({node, ...props}) => <div style={{ overflowX: 'auto', marginBottom: '1rem' }}><table style={{ width: '100%', borderCollapse: 'collapse' }} {...props} /></div>,
                                    thead: ({node, ...props}) => <thead style={{ background: '#f9fafb' }} {...props} />,
                                    th: ({node, ...props}) => <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e5e7eb', color: '#374151' }} {...props} />,
                                    td: ({node, ...props}) => <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', color: '#4b5563' }} {...props} />,
                                    a: ({node, ...props}) => <a style={{ color: '#f38020', textDecoration: 'underline', fontWeight: 500 }} target="_blank" rel="noopener noreferrer" {...props} />,
                                    hr: ({node, ...props}) => <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '2rem 0' }} {...props} />,
                                    strong: ({node, ...props}) => <strong style={{ fontWeight: 600, color: '#111827' }} {...props} />,
                                    em: ({node, ...props}) => <em style={{ fontStyle: 'italic', color: '#6b7280' }} {...props} />
                                }}
                            >
                                {content}
                            </ReactMarkdown>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Documentation;
